import { createServerFn } from "@tanstack/react-start";
import {
  type BlogLocaleCode,
  normalizeLangCode,
  readingMinutes as computeReadMinutes,
  siblingSlugs,
  stripLangSuffix,
  localeFromSlug,
} from "./blog-locales";

export type BlogListItem = {
  slug: string;
  title: string;
  excerpt: string | null;
  hero_image_url: string | null;
  created_at: string;
  /** Estimated reading time in minutes (0 when unknown). */
  read_minutes: number;
};

export type BlogArticle = BlogListItem & {
  meta_description: string | null;
  content_html: string | null;
  updated_at: string | null;
  /** Pre-stringified JSON-LD, ready to inject into a script tag. */
  json_ld: string | null;
  faq_json_ld: string | null;
  keywords: string[] | null;
  language: string | null;
  translated_from_slug: string | null;
  /** All live sibling translations (including self), keyed by locale code. */
  alternates: Array<{ locale: string; slug: string }>;
};

const rest = async <T,>(path: string): Promise<T> => {
  const url = process.env["SUPABASE_URL"];
  const key =
    process.env["SUPABASE_PUBLISHABLE_KEY"] ?? process.env["SUPABASE_ANON_KEY"];
  if (!url || !key) throw new Error("Backend not configured");
  const res = await fetch(`${url}/rest/v1/${path}`, {
    headers: { apikey: key, Accept: "application/json" },
  });
  if (!res.ok) throw new Error(`Failed to load articles (${res.status})`);
  return (await res.json()) as T;
};

const LIST_COLS = "slug,title,excerpt,hero_image_url,created_at";

/** Articles go live at 12:00 PM ET on their creation date (publish_at). */
const liveFilter = () =>
  `publish_at=lte.${encodeURIComponent(new Date().toISOString())}`;

export const listBlogArticles = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => ({
    language: normalizeLangCode(input),
  }))
  .handler(async ({ data }): Promise<BlogListItem[]> => {
    const lang = data.language satisfies BlogLocaleCode;
    const rows = await rest<
      Array<
        Omit<BlogListItem, "read_minutes"> & {
          content_html?: string | null;
        }
      >
    >(
      `blog_articles?select=${LIST_COLS},content_html&${liveFilter()}&language=eq.${lang}&order=created_at.desc&limit=200`,
    );
    return rows.map(({ content_html, ...rest_ }) => ({
      ...rest_,
      read_minutes: content_html
        ? computeReadMinutes(content_html, lang)
        : 0,
    }));
  });

export const getBlogArticle = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => {
    const slug = String(
      (input as { slug?: unknown } | undefined)?.slug ?? "",
    )
      .trim()
      .slice(0, 200);
    if (!/^[a-z0-9-]+$/i.test(slug)) throw new Error("Invalid slug");
    return { slug };
  })
  .handler(async ({ data }): Promise<BlogArticle | null> => {
    const rows = await rest<
      Array<
        Omit<
          BlogArticle,
          "json_ld" | "faq_json_ld" | "alternates" | "read_minutes"
        > & {
          json_ld: unknown;
          faq_json_ld: unknown;
        }
      >
    >(
      `blog_articles?select=${LIST_COLS},updated_at,meta_description,content_html,json_ld,faq_json_ld,keywords,language,translated_from_slug&${liveFilter()}&slug=eq.${encodeURIComponent(data.slug)}&limit=1`,
    );
    const row = rows[0];
    if (!row) return null;
    const asString = (v: unknown): string | null => {
      if (v === null || v === undefined) return null;
      if (typeof v === "string") return v.trim() ? v : null;
      try {
        return JSON.stringify(v);
      } catch {
        return null;
      }
    };

    // Look up all sibling translations so the page can offer a language switch.
    const currentLocale = localeFromSlug(data.slug);
    const siblings = siblingSlugs(
      data.slug,
      row.language,
      row.translated_from_slug,
    ).filter((s) => s.slug !== data.slug);

    let alternates: Array<{ locale: string; slug: string }> = [
      { locale: currentLocale, slug: data.slug },
    ];

    if (siblings.length > 0) {
      try {
        const inFilter = siblings
          .map((s) => encodeURIComponent(s.slug))
          .join(",");
        const altRows = await rest<Array<{ slug: string }>>(
          `blog_articles?select=slug&${liveFilter()}&slug=in.(${inFilter})&limit=20`,
        );
        const liveSlugs = new Set(altRows.map((r) => r.slug));
        for (const s of siblings) {
          if (liveSlugs.has(s.slug)) {
            alternates.push({ locale: s.locale, slug: s.slug });
          }
        }
      } catch {
        // If the sibling lookup fails, just show the current article.
      }
    }

    return {
      ...row,
      read_minutes: computeReadMinutes(row.content_html, currentLocale),
      json_ld: asString(row.json_ld),
      faq_json_ld: asString(row.faq_json_ld),
      alternates,
    };
  });

const STOP_WORDS = new Set([
  "the", "and", "for", "with", "your", "our", "how", "why", "what", "when",
  "ny", "new", "york", "pressure", "washing", "cleaning", "service",
  "services", "capital", "region", "near", "you", "best", "guide",
]);

const topicTokens = (topic: string): string[] =>
  Array.from(
    new Set(
      topic
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, " ")
        .split(/[\s-]+/)
        .filter((w) => w.length > 2 && !STOP_WORDS.has(w)),
    ),
  );

/**
 * Up to 3 English articles most relevant to a service/city topic.
 * Used to give every article inbound internal links from money pages.
 */
export const listRelatedArticles = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => ({
    topic: String((input as { topic?: unknown } | undefined)?.topic ?? "")
      .slice(0, 120),
  }))
  .handler(async ({ data }): Promise<BlogListItem[]> => {
    let rows: Array<Omit<BlogListItem, "read_minutes"> & { keywords?: string[] | null }>;
    try {
      rows = await rest(
        `blog_articles?select=${LIST_COLS},keywords&${liveFilter()}&language=eq.en&order=created_at.desc&limit=60`,
      );
    } catch {
      return [];
    }
    const tokens = topicTokens(data.topic);
    const scored = rows.map((r) => {
      const haystack = `${r.title} ${r.excerpt ?? ""} ${(r.keywords ?? []).join(" ")}`.toLowerCase();
      let score = 0;
      for (const t of tokens) if (haystack.includes(t)) score += 1;
      return { r, score };
    });
    scored.sort((a, b) => b.score - a.score);
    return scored
      .slice(0, 3)
      .map(({ r }) => ({
        slug: r.slug,
        title: r.title,
        excerpt: r.excerpt,
        hero_image_url: r.hero_image_url,
        created_at: r.created_at,
        read_minutes: 0,
      }));
  });
